import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux"; // Import Redux hooks
import { setTimeSpend } from "../features/assessment/data/redux/AssessmentSlice"; // Import Redux action
import "./../styles/AssessmentTimerStyle.css";
import button from "../assets/images/assessment/buttonEdited.png";
import { RootState } from "../store/store";
import { LocalStorage } from "../utils/Utils";
import strings from "../utils/Strings";
import CommonUtils from "../utils/Common_Utils";

interface TimerProps {
  duration: number; // Total duration in minutes
  onTimeUp?: () => void; // Callback when timer reaches 0
}

const AssessmentTimer = forwardRef((props: TimerProps, ref) => {
  const { duration, onTimeUp } = props;
  const hasTimeUpFiredRef = useRef(false); // prevents multiple submits

  const durationRedux = useSelector(
    (state: RootState) => state.assessment.duration,
  );
  const [timeSpentInSeconds, setTimeSpentInSeconds] = useState(0);
  const dispatch = useDispatch(); // Redux dispatch
  const [timeLeft, setTimeLeft] = useState(durationRedux * 60); // Timer in seconds convert
  const [isRunning, setIsRunning] = useState(true); // current timer running state

  useImperativeHandle(ref, () => ({
    resume: () => setIsRunning(true),
    pause: () => setIsRunning(false),
    decrement: (seconds: number) => {
      setTimeLeft((prevTimeLeft) => prevTimeLeft - seconds);
    },
  }));

  //To Auto Log Out when Assesment End Time Reached
  const getRemainingTimeMs = (): number => {
    const item = {
      assessment_start_time:
        LocalStorage.getLocalData(
          strings.localStorage_keys.assessment_details.assessment_start_time,
        ) ?? "",

      assessment_expiry:
        Number(
          LocalStorage.getLocalData(
            strings.localStorage_keys.assessment_details.assessment_expiry_time,
          ),
        ) || 0, // seconds

      status:
        LocalStorage.getLocalData(
          strings.localStorage_keys.assessment_details
            .candidate_assessment_status,
        ) ?? "",

      assessment_duration:
        Number(
          LocalStorage.getLocalData(
            strings.localStorage_keys.assessment_details.duration,
          ),
        ) || 0, // minutes

      time_spend:
        Number(
          LocalStorage.getLocalData(
            strings.localStorage_keys.assessment_details.time_spend,
          ),
        ) || 0, // seconds
    };

    const currentTime = new Date();

    // Convert UTC start time to local
    const assessmentStartTime = CommonUtils.parseUtcToLocalDate(
      item.assessment_start_time,
    );

    // Assessment End Time = start_time + expiry
    const assessmentEndTime = new Date(
      assessmentStartTime.getTime() + item.assessment_expiry * 1000,
    );

    let candidateEndTime: Date;

    if (item.status === "Resume") {
      // Total allowed duration in ms
      const totalDurationMs = item.assessment_duration * 60 * 1000;

      // Time already spent in ms
      const timeSpentMs = item.time_spend * 1000;

      // Remaining time = duration - time spent
      const remainingMs = Math.max(0, totalDurationMs - timeSpentMs);

      // Candidate End Time = now + remaining time
      candidateEndTime = new Date(currentTime.getTime() + remainingMs);
    } else {
      // Fresh start → full duration
      candidateEndTime = new Date(
        currentTime.getTime() + item.assessment_duration * 60 * 1000,
      );
    }

    // Remaining time = min(candidate end, assessment end) - current time
    return Math.max(
      0,
      Math.min(candidateEndTime.getTime(), assessmentEndTime.getTime()) -
        currentTime.getTime(),
    );
  };
  useEffect(() => {
    //Candidate - Assessment Auto Submit With End Time Reaches before Duration Complete
    const remainingTimeMs = getRemainingTimeMs();

    const timerId = setTimeout(() => {
      // use latest values instead of stale state

      if (!hasTimeUpFiredRef.current) {
        hasTimeUpFiredRef.current = true;
        onTimeUp && onTimeUp(); // submit API
      }
    }, remainingTimeMs);

    return () => {
      clearTimeout(timerId);
    };
  }, []);
  // // Update timeLeft if duration prop changes
  useEffect(() => {
    // setTimeLeft(duration * 60);

    const timeSpent = LocalStorage.getLocalData("time_spend");
    if (timeSpent) {
      // If time_spend key is present in localStorage, start the timer with remaining time
      setTimeLeft(duration * 60 - parseInt(timeSpent, 10));
    } else {
      // If not present start timer with full duration
      setTimeLeft(duration * 60);
    }
  }, [duration]);

  useEffect(() => {
    if (!isRunning) return;
    const timer = setInterval(() => {
      setTimeLeft((prevTimeLeft) => {
        if (prevTimeLeft <= 1) {
          clearInterval(timer);
          if (onTimeUp) {
            if (!hasTimeUpFiredRef.current) {
              hasTimeUpFiredRef.current = true;
              onTimeUp && onTimeUp(); // submit API
            }
          }
          return 0;
        }
        return prevTimeLeft - 1;
      });
      setTimeSpentInSeconds(durationRedux * 60 - timeLeft); // Total elapsed time
      dispatch(setTimeSpend(timeSpentInSeconds));
    }, 900);
    return () => clearInterval(timer);
  }, [isRunning, onTimeUp, timeLeft, dispatch, duration, durationRedux]);

  // Utility function to calculate timer state
  // function calculateTimerState(timeLeft: number, duration: number) {
  //   const minutes = Math.floor(timeLeft / 60);
  //   const seconds = timeLeft % 60;
  //   const percentageLeft = (timeLeft / (duration * 60)) * 100;
  //   let timerColor = "#00BA4A";
  //   let boxShadow = "";
  //   if (percentageLeft <= 15) {
  //     timerColor = "red";
  //     boxShadow = "1px 1px 6px 8px rgba(237, 35, 35, 0.68)";
  //   } else if (percentageLeft <= 30) {
  //     timerColor = "rgba(232, 155, 54, 0.93)";
  //   }
  //   return { minutes, seconds, timerColor, boxShadow };
  // }

  function calculateTimerState(timeLeft: number, duration: number) {
    // const minutes = Math.floor(timeLeft / 60);
    // const seconds = timeLeft % 60;
    const hours = Math.floor(timeLeft / 3600);
    const minutes = Math.floor((timeLeft % 3600) / 60);
    const seconds = timeLeft % 60;
    const percentageLeft = (timeLeft / (duration * 60)) * 100;

    let timerColor = "#00BA4A";
    let bgColor = "#E6F7EE"; // green soft bg

    if (percentageLeft <= 15) {
      timerColor = "#FF3B30";
      bgColor = "#FFEAEA"; // red soft
    } else if (percentageLeft <= 30) {
      timerColor = "#E89B36";
      bgColor = "#FFF4E5"; // yellow soft
    }
    return { hours, minutes, seconds, timerColor, bgColor };
    // return { minutes, seconds, timerColor, bgColor };
  }
  // const { minutes, seconds, timerColor, bgColor } = calculateTimerState(
  //   timeLeft,
  //   duration,
  // );
  const { hours, minutes, seconds, timerColor, bgColor } = calculateTimerState(
    timeLeft,
    duration,
  );

  return (
    // <div className="blueContainer" style={{ boxShadow }}>
    //   <div className="blueCards">
    //     <img
    //       src={button}
    //       alt="Button 1"
    //       className="blueBox"
    //       style={{ borderRadius: "10px" }}
    //     />
    //     <img src={button} alt="Button 2" className="blueBox" />
    //   </div>
    //   <div className="whiteContainer">
    //     <div className="TimerContain">
    //       <div className="Time" style={{ color: timerColor }}>
    //         {/* {minutes}:{seconds < 10 ? `0${seconds}` : seconds} */}
    //         {Math.max(minutes, 0)}:
    //         {seconds < 10 ? `0${Math.max(seconds, 0)}` : Math.max(seconds, 0)}
    //       </div>
    //       <div className="MinutesLeft">minutes Left</div>
    //     </div>
    //   </div>
    //   <div className="bottomBlueBox">
    //     <img
    //       src={button}
    //       alt="Button 1"
    //       className="blueBox"
    //       style={{ borderRadius: "10px" }}
    //     />
    //     <img src={button} alt="Button 2" className="blueBox" />
    //   </div>
    // </div>
    <div className="blueContainer">
      <div className="whiteContainer" style={{ background: bgColor }}>
        <div className="TimerContain">
          <div className="Time" style={{ color: timerColor }}>
            {/* {Math.max(minutes, 0)}:
            {seconds < 10 ? `0${Math.max(seconds, 0)}` : Math.max(seconds, 0)} */}
            {hours > 0 ? `${hours.toString().padStart(2, "0")}:` : ""}
            {minutes.toString().padStart(2, "0")}:
            {seconds.toString().padStart(2, "0")}
          </div>

          {/* Show only if less than 1 hour */}
          {/* {<div className="MinutesLeft">minutes left</div>} */}
          {hours === 0 && <div className="MinutesLeft">minutes left</div>}
        </div>
      </div>
    </div>
  );
});

export default AssessmentTimer;
